/**
 * PropBrowserFilters
 * Browse-tab filter bar: league toggle, search box, prop-type dropdown,
 * alt-lines toggle, and scrollable game chips.
 */

import { useScrollOverflow } from '@hooks/useScrollOverflow';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { leagueToLeagueKey, SUPPORTED_LEAGUES } from '@parlay-picker/shared/league';
import { getTeamAbbr, getTeamLogo } from '@parlay-picker/shared/teams';
import type { Game } from '@utils/propUtils';
import { motion } from 'framer-motion';
import { type ChangeEvent, type ReactElement, useRef } from 'react';
import type { PropTypeSection } from './usePropTypesBySection';

interface PropBrowserFiltersProps {
  selectedLeagueKey: LeagueKey;
  onLeagueChange: (lk: LeagueKey) => void;
  search: string;
  onSearchChange: (value: string) => void;
  propTypeFilter: string;
  onPropTypeFilterChange: (value: string) => void;
  propTypesBySection: PropTypeSection[];
  showAlts: boolean;
  onShowAltsToggle: () => void;
  games: Game[];
  gameFilter: string;
  onGameFilterChange: (gameId: string) => void;
}

export function PropBrowserFilters({
  selectedLeagueKey,
  onLeagueChange,
  search,
  onSearchChange,
  propTypeFilter,
  onPropTypeFilterChange,
  propTypesBySection,
  showAlts,
  onShowAltsToggle,
  games,
  gameFilter,
  onGameFilterChange,
}: PropBrowserFiltersProps): ReactElement {
  const gameChipsRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: gameChipsOverflow } = useScrollOverflow(gameChipsRef);

  return (
    <div className="px-6 py-3 border-b shrink-0 max-h-[30vh] overflow-y-auto border-theme-border/50 bg-theme-bg/50">
      <div className="flex flex-wrap gap-2">
        {/* League toggle */}
        <div className="flex rounded-lg border border-theme-border overflow-hidden shrink-0">
          {SUPPORTED_LEAGUES.map((league) => {
            const lk = leagueToLeagueKey(league);
            const isActive = lk === selectedLeagueKey;
            return (
              <button
                type="button"
                key={league}
                onClick={() => onLeagueChange(lk)}
                className={`px-3 py-2 text-xs font-semibold transition ${
                  isActive
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'bg-theme-bg text-theme-text-tertiary hover:text-white hover:bg-theme-surface-hover'
                }`}
              >
                {league}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <input
          type="text"
          data-testid="prop-browser-search-input"
          placeholder="Search players, teams..."
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          className="flex-1 min-w-48 px-3 py-2 border rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none bg-theme-bg border-theme-border focus:border-theme-primary"
        />

        {/* Prop type filter */}
        <select
          data-testid="prop-browser-prop-type-filter"
          value={propTypeFilter}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => onPropTypeFilterChange(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm text-white focus:outline-none bg-theme-bg border-theme-border focus:border-theme-primary"
        >
          <option value="all">All Props</option>
          {propTypesBySection.map((section) => (
            <optgroup key={section.label} label={section.label}>
              {section.items.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {/* Alt lines toggle */}
        <button
          type="button"
          onClick={onShowAltsToggle}
          className={
            showAlts
              ? 'shrink-0 px-3 py-2 border rounded-lg text-xs font-medium transition bg-theme-primary/20 border-theme-primary text-theme-primary'
              : 'shrink-0 px-3 py-2 border rounded-lg text-xs font-medium transition bg-theme-bg border-theme-border text-theme-text-tertiary hover:text-white'
          }
          title={showAlts ? 'Showing alternate lines' : 'Alt lines hidden'}
        >
          Alt Lines {showAlts ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Game chips */}
      {games.length > 0 && (
        <div className="relative">
          {gameChipsOverflow && (
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-800 to-transparent z-10 pointer-events-none" />
          )}
          <div
            ref={gameChipsRef}
            className="flex gap-1.5 mt-2 overflow-x-auto px-0.5 py-1 scrollbar-none"
          >
            <button
              type="button"
              onClick={() => onGameFilterChange('all')}
              className="relative shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium transition"
            >
              {gameFilter === 'all' && (
                <motion.div
                  layoutId="activeGameChip"
                  className="absolute inset-0 rounded-lg bg-theme-primary/20 ring-1 ring-theme-primary"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span
                className={`relative z-10 ${
                  gameFilter === 'all'
                    ? 'text-theme-primary'
                    : 'text-theme-text-tertiary hover:text-white'
                }`}
              >
                All
              </span>
            </button>
            {games.map((game) => {
              const awayAbbr = getTeamAbbr(game.away_team, selectedLeagueKey);
              const homeAbbr = getTeamAbbr(game.home_team, selectedLeagueKey);
              const isActive = gameFilter === game.id;
              return (
                <button
                  type="button"
                  key={game.id}
                  data-testid="prop-browser-game-chip"
                  onClick={() => onGameFilterChange(game.id === gameFilter ? 'all' : game.id)}
                  className={`relative shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                    !isActive
                      ? 'bg-theme-surface text-theme-text-tertiary hover:bg-theme-surface-hover hover:text-white'
                      : ''
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeGameChip"
                      className="absolute inset-0 rounded-lg bg-theme-primary/20 ring-1 ring-theme-primary"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <img
                    src={getTeamLogo(game.away_team, selectedLeagueKey)}
                    alt=""
                    className="relative z-10 w-4 h-4"
                    loading="lazy"
                  />
                  <span className="relative z-10">
                    {awayAbbr}@{homeAbbr}
                  </span>
                  <img
                    src={getTeamLogo(game.home_team, selectedLeagueKey)}
                    alt=""
                    className="relative z-10 w-4 h-4"
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
